import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, FolderKanban, LogOut, Users, LayoutDashboard, User, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const projectColors = [
  { value: '#0073ea', label: 'Blau' },
  { value: '#00c875', label: 'Grün' },
  { value: '#fdab3d', label: 'Orange' },
  { value: '#e44258', label: 'Rot' },
  { value: '#a25ddc', label: 'Lila' },
  { value: '#ff5ac4', label: 'Pink' },
  { value: '#00d2d3', label: 'Türkis' },
];

const Dashboard = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    color: '#0073ea',
    assigned_users: []
  });

  useEffect(() => {
    fetchProjects();
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: getAuthHeader()
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Projekte');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data.filter(u => u.role === 'kunde'));
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, newProject, {
        headers: getAuthHeader()
      });
      toast.success('Projekt erfolgreich erstellt!');
      setIsCreateDialogOpen(false);
      setNewProject({ name: '', description: '', color: '#0073ea', assigned_users: [] });
      fetchProjects();
    } catch (error) {
      toast.error('Fehler beim Erstellen des Projekts');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleEditProject = (project) => {
    setEditingProject({
      id: project.id,
      name: project.name,
      description: project.description,
      color: project.color,
      assigned_users: project.assigned_users
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/projects/${editingProject.id}`, {
        name: editingProject.name,
        description: editingProject.description,
        color: editingProject.color,
        assigned_users: editingProject.assigned_users
      }, {
        headers: getAuthHeader()
      });
      toast.success('Projekt erfolgreich aktualisiert!');
      setIsEditDialogOpen(false);
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Projekts');
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f7fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                <LayoutDashboard className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800">Tasky</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-800">{user?.username}</p>
                <p className="text-xs text-gray-500">{user?.role === 'admin' ? 'Administrator' : 'Kunde'}</p>
              </div>
              <Button
                variant="outline"
                data-testid="profile-button"
                onClick={() => navigate('/profile')}
                className="flex items-center space-x-2"
              >
                <User className="w-4 h-4" />
                <span>Profil</span>
              </Button>
              {user?.role === 'admin' && (
                <Button
                  variant="outline"
                  data-testid="admin-panel-button"
                  onClick={() => navigate('/admin')}
                  className="flex items-center space-x-2"
                >
                  <Users className="w-4 h-4" />
                  <span>Admin-Panel</span>
                </Button>
              )}
              <Button
                variant="outline"
                data-testid="logout-button"
                onClick={handleLogout}
                className="flex items-center space-x-2"
              >
                <LogOut className="w-4 h-4" />
                <span>Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-800">Projekte</h2>
            <p className="text-gray-500 mt-1">Verwalten Sie Ihre Projekte und Tasks</p>
          </div>
          {user?.role === 'admin' && (
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-project-button" className="bg-[#0073ea] hover:bg-[#0060c0] text-white flex items-center space-x-2">
                  <Plus className="w-4 h-4" />
                  <span>Neues Projekt</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]" aria-describedby="dialog-project-description">
                <DialogHeader>
                  <DialogTitle>Neues Projekt erstellen</DialogTitle>
                  <DialogDescription id="dialog-project-description">
                    Erstellen Sie ein neues Projekt und weisen Sie Benutzer zu.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="project-name">Projektname</Label>
                    <Input
                      id="project-name"
                      data-testid="project-name-input"
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="project-description">Beschreibung</Label>
                    <Textarea
                      id="project-description"
                      data-testid="project-description-input"
                      value={newProject.description}
                      onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="project-color">Projektfarbe</Label>
                    <Select
                      value={newProject.color}
                      onValueChange={(value) => setNewProject({ ...newProject, color: value })}
                    >
                      <SelectTrigger className="mt-2" data-testid="project-color-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {projectColors.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 rounded" style={{ backgroundColor: color.value }}></div>
                              <span>{color.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="assigned-users">Zugewiesene Benutzer</Label>
                    <Select
                      value={newProject.assigned_users[0] || ''}
                      onValueChange={(value) => {
                        const currentUsers = newProject.assigned_users;
                        if (value && !currentUsers.includes(value)) {
                          setNewProject({ ...newProject, assigned_users: [...currentUsers, value] });
                        }
                      }}
                    >
                      <SelectTrigger className="mt-2" data-testid="assigned-users-select">
                        <SelectValue placeholder="Benutzer auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.username} ({u.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {newProject.assigned_users.map((userId) => {
                        const foundUser = users.find(u => u.id === userId);
                        return foundUser ? (
                          <span key={userId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                            {foundUser.username}
                            <button
                              type="button"
                              onClick={() => setNewProject({
                                ...newProject,
                                assigned_users: newProject.assigned_users.filter(id => id !== userId)
                              })}
                              className="ml-2 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button type="submit" data-testid="create-project-submit-button" className="bg-[#0073ea] hover:bg-[#0060c0] text-white">
                      Erstellen
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderKanban className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Keine Projekte vorhanden</h3>
            <p className="text-gray-400">
              {user?.role === 'admin'
                ? 'Erstellen Sie Ihr erstes Projekt'
                : 'Es wurden Ihnen noch keine Projekte zugewiesen'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card
                key={project.id}
                data-testid={`project-card-${project.id}`}
                className="p-6 hover:shadow-lg border-t-4 relative"
                style={{ borderTopColor: project.color }}
              >
                <div onClick={() => navigate(`/project/${project.id}`)} className="cursor-pointer">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: project.color }}>
                        <FolderKanban className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">{project.name}</h3>
                      </div>
                    </div>
                  </div>
                  {project.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{project.description}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{project.assigned_users.length} Benutzer</span>
                    <span>{new Date(project.created_at).toLocaleDateString('de-DE')}</span>
                  </div>
                </div>
                {user?.role === 'admin' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`edit-project-${project.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditProject(project);
                    }}
                    className="absolute top-4 right-4 text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Edit Project Dialog */}
      {editingProject && (
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[500px]" aria-describedby="dialog-edit-project-description">
            <DialogHeader>
              <DialogTitle>Projekt bearbeiten</DialogTitle>
              <DialogDescription id="dialog-edit-project-description">
                Bearbeiten Sie die Projektdetails.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateProject} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="edit-project-name">Projektname</Label>
                <Input
                  id="edit-project-name"
                  data-testid="edit-project-name-input"
                  value={editingProject.name}
                  onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                  required
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="edit-project-description">Beschreibung</Label>
                <Textarea
                  id="edit-project-description"
                  data-testid="edit-project-description-input"
                  value={editingProject.description}
                  onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                  className="mt-2"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="edit-project-color">Projektfarbe</Label>
                <Select
                  value={editingProject.color}
                  onValueChange={(value) => setEditingProject({ ...editingProject, color: value })}
                >
                  <SelectTrigger className="mt-2" data-testid="edit-project-color-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {projectColors.map((color) => (
                      <SelectItem key={color.value} value={color.value}>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: color.value }}></div>
                          <span>{color.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-assigned-users">Zugewiesene Benutzer</Label>
                <Select
                  value=""
                  onValueChange={(value) => {
                    if (value && !editingProject.assigned_users.includes(value)) {
                      setEditingProject({ 
                        ...editingProject, 
                        assigned_users: [...editingProject.assigned_users, value] 
                      });
                    }
                  }}
                >
                  <SelectTrigger className="mt-2" data-testid="edit-assigned-users-select">
                    <SelectValue placeholder="Benutzer auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.username} ({u.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-2 flex flex-wrap gap-2">
                  {editingProject.assigned_users.map((userId) => {
                    const foundUser = users.find(u => u.id === userId);
                    return foundUser ? (
                      <span key={userId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                        {foundUser.username}
                        <button
                          type="button"
                          onClick={() => setEditingProject({
                            ...editingProject,
                            assigned_users: editingProject.assigned_users.filter(id => id !== userId)
                          })}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" data-testid="update-project-submit-button" className="bg-[#0073ea] hover:bg-[#0060c0] text-white">
                  Aktualisieren
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Dashboard;
